import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = [
    // Progress indicators
    "stepLabel", "stepCounter", "progressBar",
    // Navigation
    "step", "prevButton", "nextButton", "navigation",
    // Step 1: Product Basics
    "productName", "productNameError", "categoryGrid", "categoryId", "categoryError",
    // Step 2: Pricing
    "price", "priceError", "unitGrid", "unit", "quantity", "quantityError", "pricePreview",
    // Step 3: Location
    "locationSearch", "locationResults", "locationError", "selectedLocation", "selectedLocationText",
    "locationCity", "locationRegion", "locationCityEn", "locationCityEl",
    "locationRegionEn", "locationRegionEl", "latitude", "longitude", "pickupDetails",
    // Step 4: Photos
    "photoInput", "photoPreviewsContainer", "photoPreviews", "photoCount",
    // Step 5: Preview
    "productCard", "previewImageContainer", "previewImage", "previewImagePlaceholder", "previewTitle",
    "previewPrice", "previewUnit", "previewLocation", "previewCategory", "previewQuantity",
    // Step 6: Go Live
    "emailToggleIcon", "emailForm", "email", "password", "emailError", "emailSubmitButton", "loginToggle"
  ]

  static values = {
    categories: Array,
    units: Array,
    mapboxToken: String,
    locale: String,
    translations: Object,
    country: { type: String, default: "" },
    proximity: { type: String, default: "" },
    currencySymbol: { type: String, default: "€" },
    directUploadUrl: String
  }

  connect() {
    this.currentStep = 1
    this.totalSteps = 6
    this.draft = this.loadDraft()
    this.photos = []
    this.photoBlobs = []
    this.debounceTimer = null
    this.isLoginMode = false

    // Restore draft if exists
    if (this.draft) {
      this.restoreDraft()
    }

    // Always ensure UI matches current step (in case restoreDraft had issues)
    this.updateUI()

    // Close location results when clicking outside
    document.addEventListener('click', this.handleClickOutside.bind(this))
  }

  disconnect() {
    document.removeEventListener('click', this.handleClickOutside.bind(this))
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  // ===========================================
  // NAVIGATION
  // ===========================================

  nextStep() {
    if (!this.validateCurrentStep()) {
      // Validation failed. The Next button lives at the bottom of the step,
      // but the invalid field is usually at the top — without scrolling the
      // user sees NOTHING change and thinks the button is broken. Scroll to
      // the first invalid field (red border set by showError) and focus it
      // so the on-screen keyboard opens on mobile.
      this.scrollToFirstError()
      return
    }

    if (this.currentStep < this.totalSteps) {
      this.currentStep++
      this.updateUI()
      this.saveDraft()
      this.trackStep()

      // Special handling for preview step
      if (this.currentStep === 5) {
        this.updateProductPreview()
      }
    }
  }

  // Per-step funnel instrumentation. Fires a Plausible event every time the
  // user REACHES a new step (i.e. they filled the previous step and advanced),
  // so we can see exactly where onboarding drops off:
  //   StartOnboard (load) → OnboardStep{2..6} → CompleteRegistration (signup)
  // One event name + a `step` prop keeps Plausible to a single goal with a
  // breakdown. try/catch so a stalled analytics SDK can never block the wizard.
  trackStep() {
    try {
      if (typeof plausible !== 'undefined') {
        const props = JSON.parse(document.body.dataset.plausibleProps || '{}')
        // step MUST be a string — Plausible drops events whose custom-property
        // value is a raw number, which is why OnboardStep recorded 0 while the
        // string-prop events (StartOnboard, LandingPageCTA) worked fine.
        plausible('OnboardStep', { props: { ...props, step: String(this.currentStep) } })
      }
    } catch (e) { /* tracking never blocks onboarding */ }
  }

  // Find the first error inside the currently visible step and scroll it
  // into view. "Error" can mean either a red-bordered input (product name,
  // price, quantity — set by showError) OR a visible red-text paragraph
  // (category, location — those don't have a bordered input to color, so
  // showError writes to a <p data-target="...Error">). We check inputs
  // first because focusing a real input is more useful than jumping to
  // an error message, but fall back to any visible error paragraph.
  //
  // Mobile-specific behavior:
  //   - iOS Safari opens the on-screen keyboard on programmatic focus()
  //     ONLY when the call is synchronous inside the user-gesture handler
  //     chain (the tap on the Next button). Wrapping focus in setTimeout
  //     breaks the chain — the keyboard would NOT reopen. So we focus
  //     synchronously here, BEFORE the scroll animation begins.
  //   - preventScroll: true stops the browser's default focus-triggered
  //     jump-scroll from fighting our smooth scrollIntoView.
  //   - When the keyboard opens on mobile, the visual viewport shrinks;
  //     the subsequent scrollIntoView calculates against that smaller
  //     viewport, so the field ends up in the center of the actually-
  //     visible area (above the keyboard) — which is exactly what we
  //     want.
  scrollToFirstError() {
    const activeStep = this.stepTargets[this.currentStep - 1]
    if (!activeStep) return

    let target = activeStep.querySelector('.border-red-500, .border-red-600')
    if (!target) {
      // Fallback: find a visible red-text error paragraph. This catches
      // steps where the failing field is a pill grid (category, unit) or
      // a map picker (location) that doesn't get a red border.
      target = activeStep.querySelector('.text-red-500:not(.hidden), .text-red-600:not(.hidden)')
    }
    if (!target) return

    // Focus FIRST (synchronously — preserves iOS user-gesture chain so the
    // on-screen keyboard reopens on the failing field). Only for real inputs;
    // focusing a category pill or an error paragraph is either disruptive or
    // a no-op.
    if (target.matches('input:not([type="hidden"]), textarea, select')) {
      target.focus({ preventScroll: true })
    }

    // Then scroll. On mobile the keyboard is now up, viewport is shorter,
    // and 'block: center' centers within the visible area.
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  previousStep() {
    if (this.currentStep > 1) {
      this.currentStep--
      this.updateUI()
    }
  }

  goToStep(stepNumber) {
    if (stepNumber >= 1 && stepNumber <= this.totalSteps) {
      this.currentStep = stepNumber
      this.updateUI()
    }
  }

  updateUI() {
    // Update step counter FIRST (most important for user feedback)
    if (this.hasStepCounterTarget) {
      this.stepCounterTarget.textContent = this.currentStep
    }

    // Update progress bar
    if (this.hasProgressBarTarget) {
      const progressPercent = (this.currentStep / this.totalSteps) * 100
      this.progressBarTarget.style.width = `${progressPercent}%`
    }

    // Hide all steps, show current
    this.stepTargets.forEach(step => {
      const stepNum = parseInt(step.dataset.step)
      step.classList.toggle('hidden', stepNum !== this.currentStep)
    })

    // Update step label text
    const stepKeys = ['product_basics', 'pricing', 'location', 'photos', 'preview', 'go_live']
    if (this.hasStepLabelTarget && this.translationsValue?.steps) {
      this.stepLabelTarget.textContent = this.translationsValue.steps[stepKeys[this.currentStep - 1]]
    }

    // Update navigation buttons
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.classList.toggle('hidden', this.currentStep === 1)
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle('hidden', this.currentStep === 6)
    }
    if (this.hasNavigationTarget) {
      this.navigationTarget.classList.toggle('hidden', this.currentStep === 6)
    }

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // ===========================================
  // VALIDATION
  // ===========================================

  validateCurrentStep() {
    switch (this.currentStep) {
      case 1:
        return this.validateStep1()
      case 2:
        return this.validateStep2()
      case 3:
        return this.validateStep3()
      case 4:
        return true // Photos are optional
      case 5:
        return true // Preview is just display
      default:
        return true
    }
  }

  // Called from the product-name input's `input` and `focusout` actions —
  // mirrors the product-form controller's live validation pattern so the
  // red border and error text CLEAR as soon as the user types a valid
  // value. Without this, after fixing "hhhhh" to "karota" the error would
  // stay on screen until the user clicked Next again — which is the exact
  // bug from testing on 2026-07-11.
  //
  // On a valid input we swap the red border for a green one and clear the
  // error text — same visual affordance as the main product form so the
  // user sees an immediate "yes this works" signal, not just an absence
  // of an error.
  validateProductNameOnInput() {
    const input = this.productNameTarget
    const errorEl = this.productNameErrorTarget
    const isEmpty = !input.value.trim()

    if (isEmpty) {
      // Fresh / cleared field: reset to neutral. No error and no premature
      // "green success" state — that would be misleading before they've
      // typed anything.
      input.classList.remove('border-red-500', 'border-green-500')
      errorEl.classList.add('hidden')
      return
    }

    const nameError = this.getProductNameError()
    if (nameError) {
      input.classList.remove('border-green-500')
      this.showError(input, errorEl, nameError)
    } else {
      input.classList.remove('border-red-500')
      input.classList.add('border-green-500')
      errorEl.classList.add('hidden')
    }
  }

  validateStep1() {
    let valid = true
    const v = this.translationsValue.validation

    // Product name — content-safety rules extracted into getProductNameError()
    // so this validator (fires on Next) and validateProductNameOnInput()
    // (fires on every keystroke) apply IDENTICAL rules. Divergence would
    // let the wizard advance past a bad name — or worse, keep flagging a
    // now-valid name until Next is clicked again.
    const nameError = this.getProductNameError()
    if (nameError) {
      this.showError(this.productNameTarget, this.productNameErrorTarget, nameError)
      valid = false
    } else {
      this.clearError(this.productNameTarget, this.productNameErrorTarget)
    }

    // Category
    const categoryId = this.categoryIdTarget.value
    if (!categoryId) {
      this.categoryErrorTarget.textContent = v.category_required
      this.categoryErrorTarget.classList.remove('hidden')
      valid = false
    } else {
      this.categoryErrorTarget.classList.add('hidden')
    }

    return valid
  }

  // Pure content-safety check for the product name — returns an error
  // message string (from the wizard's own validation i18n namespace) or
  // null. No UI side effects; both live-input and Next-click validators
  // read from this so the rules can't drift.
  //
  // Regex constants MUST match app/models/product.rb and
  // app/javascript/controllers/product_form_controller.js. Same three
  // enforcement surfaces means the user gets the same rejection no matter
  // which form they type into or whether they bypass the client entirely.
  getProductNameError() {
    const TITLE_ALLOWED_CHARS = /^[\p{L}\p{M}\p{N}\s\-'&.,%()\/]+$/u // \p{M}: Thai vowel/tone marks are combining marks, not letters
    const URL_PATTERN = /(https?:\/\/|www\.[\w-]+|\b[\w-]+\.(com|gr|net|org|io|xyz|shop|store|co|eu|uk|de|fr|it|es)\b)/i
    const EMAIL_PATTERN = /[\w.+-]+@[\w-]+\.[\w.-]+/
    const REPEATED_CHAR_PATTERN = /(.)\1{4,}/u
    const LETTER_COUNT_MIN = 2

    const v = this.translationsValue.validation
    const name = this.productNameTarget.value.trim()

    // Order matters: check most-specific violations first so the user gets
    // the clearest error message (e.g. "no email" beats "invalid chars"
    // even though an email would trigger both).
    if (!name) return v.product_name_required
    if (name.length < 3) return v.product_name_too_short || v.product_name_required
    if (name.length > 100) return v.product_name_too_long || v.product_name_required
    if (EMAIL_PATTERN.test(name)) return v.product_name_no_email || v.product_name_required
    if (URL_PATTERN.test(name)) return v.product_name_no_url || v.product_name_required
    if (!TITLE_ALLOWED_CHARS.test(name)) return v.product_name_invalid_chars || v.product_name_required
    if (REPEATED_CHAR_PATTERN.test(name)) return v.product_name_repeated_chars || v.product_name_required
    if ((name.match(/\p{L}/gu) || []).length < LETTER_COUNT_MIN) return v.product_name_no_gibberish || v.product_name_required
    return null
  }

  validateStep2() {
    let valid = true

    // Price
    const price = parseFloat(this.priceTarget.value)
    if (isNaN(price) || price <= 0) {
      this.showError(this.priceTarget, this.priceErrorTarget, this.translationsValue.validation.price_required)
      valid = false
    } else {
      this.clearError(this.priceTarget, this.priceErrorTarget)
    }

    // Quantity
    const quantity = parseInt(this.quantityTarget.value)
    if (isNaN(quantity) || quantity <= 0) {
      this.showError(this.quantityTarget, this.quantityErrorTarget, this.translationsValue.validation.quantity_required)
      valid = false
    } else {
      this.clearError(this.quantityTarget, this.quantityErrorTarget)
    }

    return valid
  }

  validateStep3() {
    const hasLocation = this.latitudeTarget.value && this.longitudeTarget.value

    if (!hasLocation) {
      this.locationErrorTarget.textContent = this.translationsValue.validation.location_required
      this.locationErrorTarget.classList.remove('hidden')
      return false
    }

    this.locationErrorTarget.classList.add('hidden')
    return true
  }

  showError(input, errorTarget, message) {
    input.classList.add('border-red-500')
    errorTarget.textContent = message
    errorTarget.classList.remove('hidden')
  }

  clearError(input, errorTarget) {
    input.classList.remove('border-red-500')
    errorTarget.classList.add('hidden')
  }

  // ===========================================
  // STEP 1: CATEGORY SELECTION
  // ===========================================

  selectCategory(event) {
    const button = event.currentTarget
    const categoryId = button.dataset.categoryId
    const categoryName = button.dataset.categoryName

    // Update selection UI
    this.categoryGridTarget.querySelectorAll('.category-pill').forEach(pill => {
      pill.classList.remove('border-primary', 'bg-primary')
      pill.classList.add('border-gray-200')
      pill.querySelector('i').classList.remove('text-white', 'opacity-80')
      pill.querySelector('i').classList.add('text-primary')
      pill.querySelector('div').classList.remove('text-white', 'opacity-80')
      pill.querySelector('div').classList.add('text-text-primary')
    })

    button.classList.remove('border-gray-200')
    button.classList.add('border-primary', 'bg-primary')
    button.querySelector('i').classList.remove('text-primary')
    button.querySelector('i').classList.add('text-white', 'opacity-80')
    button.querySelector('div').classList.remove('text-text-primary')
    button.querySelector('div').classList.add('text-white', 'opacity-80')

    // Store value
    this.categoryIdTarget.value = categoryId
    this.selectedCategoryName = categoryName

    // Clear error
    this.categoryErrorTarget.classList.add('hidden')

    this.saveDraft()
  }

  // ===========================================
  // STEP 2: UNIT SELECTION & PREVIEW
  // ===========================================

  selectUnit(event) {
    const button = event.currentTarget
    const unit = button.dataset.unit

    // Update selection UI
    this.unitGridTarget.querySelectorAll('.unit-pill').forEach(pill => {
      pill.classList.remove('border-primary', 'bg-primary', 'text-white')
      pill.classList.add('border-gray-200', 'text-text-primary')
    })

    button.classList.remove('border-gray-200', 'text-text-primary')
    button.classList.add('border-primary', 'bg-primary', 'text-white')

    // Store value
    this.unitTarget.value = unit

    this.updatePreview()
    this.saveDraft()
  }

  updatePreview() {
    const price = parseFloat(this.priceTarget.value) || 0
    const unit = this.unitTarget.value || 'kg'
    const quantity = parseInt(this.quantityTarget.value) || 0

    const unitLabel = this.getUnitLabel(unit)
    const availableText = this.localeValue === 'el' ? 'διαθέσιμα' : 'available'

    this.pricePreviewTarget.textContent = `${this.currencySymbolValue}${price.toFixed(2)} / ${unitLabel} — ${quantity} ${availableText}`
  }

  getUnitLabel(unit) {
    const labels = {
      el: { kg: 'κιλά', g: 'γρ', liter: 'λτ', ml: 'ml', piece: 'τεμάχιο', jar: 'βάζο', bottle: 'μπουκάλι', box: 'κουτί', bunch: 'ματσάκι' },
      en: { kg: 'kg', g: 'g', liter: 'L', ml: 'ml', piece: 'piece', jar: 'jar', bottle: 'bottle', box: 'box', bunch: 'bunch' }
    }
    return labels[this.localeValue]?.[unit] || unit
  }

  // ===========================================
  // STEP 3: LOCATION SEARCH (Mapbox)
  // ===========================================

  searchLocation(event) {
    const query = event.target.value.trim()

    if (query.length < 2) {
      this.hideLocationResults()
      return
    }

    if (this.debounceTimer) clearTimeout(this.debounceTimer)

    this.debounceTimer = setTimeout(() => {
      this.fetchLocationResults(query)
    }, 300)
  }

  async fetchLocationResults(query) {
    try {
      let url = `https://api.mapbox.com/search/geocode/v6/forward?` +
        `q=${encodeURIComponent(query)}&` +
        `access_token=${this.mapboxTokenValue}&` +
        `types=place,locality,neighborhood&` +
        `language=en,el&` +
        `limit=5`

      if (this.proximityValue) url += `&proximity=${this.proximityValue}`
      if (this.countryValue) url += `&country=${this.countryValue}`

      const response = await fetch(url)
      const data = await response.json()

      if (data.features && data.features.length > 0) {
        this.displayLocationResults(data.features)
      } else {
        this.showNoLocationResults()
      }
    } catch (error) {
      console.error('Mapbox API error:', error)
      this.hideLocationResults()
    }
  }

  displayLocationResults(features) {
    this.locationResultsTarget.innerHTML = features.map(feature => {
      const props = feature.properties || {}
      const coords = feature.geometry?.coordinates || [0, 0]
      const displayName = props.name || ''
      const fullAddress = props.full_address || props.place_formatted || ''

      return `
        <div class="px-4 py-3 hover:bg-accent-yellow hover:bg-opacity-20 cursor-pointer transition-colors border-b border-gray-100 last:border-0"
             data-action="click->onboarding-wizard#selectLocation"
             data-lat="${coords[1]}"
             data-lon="${coords[0]}"
             data-name="${fullAddress}"
             data-feature='${JSON.stringify(feature).replace(/'/g, "\\'")}'>
          <div class="flex items-center gap-2">
            <i class="fas fa-map-marker-alt text-primary text-sm"></i>
            <div class="flex-1">
              <div class="text-sm font-medium text-text-primary">${displayName}</div>
              <div class="text-xs text-text-primary opacity-60">${props.place_formatted || ''}</div>
            </div>
          </div>
        </div>
      `
    }).join('')

    this.locationResultsTarget.classList.remove('hidden')
  }

  showNoLocationResults() {
    const noResultsText = this.localeValue === 'el' ? 'Δεν βρέθηκαν τοποθεσίες' : 'No locations found'
    this.locationResultsTarget.innerHTML = `
      <div class="px-4 py-3 text-sm text-text-primary opacity-60 text-center">
        <i class="fas fa-search mr-2"></i>${noResultsText}
      </div>
    `
    this.locationResultsTarget.classList.remove('hidden')
  }

  hideLocationResults() {
    this.locationResultsTarget.classList.add('hidden')
  }

  selectLocation(event) {
    const element = event.currentTarget
    const lat = element.dataset.lat
    const lon = element.dataset.lon
    const name = element.dataset.name
    const feature = JSON.parse(element.dataset.feature || '{}')

    // Extract bilingual data
    const props = feature.properties || {}
    const context = props.context || {}

    let cityEn = '', cityEl = '', regionEn = '', regionEl = ''

    if (context.place) {
      cityEn = context.place.name || ''
      cityEl = context.place.name || ''
      if (context.place.translations) {
        if (context.place.translations.en) cityEn = context.place.translations.en.name
        if (context.place.translations.el) cityEl = context.place.translations.el.name
      }
    }

    if (context.region) {
      regionEn = context.region.name || ''
      regionEl = context.region.name || ''
      if (context.region.translations) {
        if (context.region.translations.en) regionEn = context.region.translations.en.name
        if (context.region.translations.el) regionEl = context.region.translations.el.name
      }
    }

    // Store values
    this.latitudeTarget.value = lat
    this.longitudeTarget.value = lon
    this.locationCityTarget.value = cityEn
    this.locationRegionTarget.value = regionEn
    this.locationCityEnTarget.value = cityEn
    this.locationCityElTarget.value = cityEl
    this.locationRegionEnTarget.value = regionEn
    this.locationRegionElTarget.value = regionEl

    // Update UI
    this.locationSearchTarget.value = name
    this.selectedLocationTextTarget.textContent = name
    this.selectedLocationTarget.classList.remove('hidden')
    this.locationErrorTarget.classList.add('hidden')

    this.hideLocationResults()
    this.saveDraft()
  }

  handleClickOutside(event) {
    if (this.hasLocationResultsTarget && !this.locationResultsTarget.contains(event.target) && !this.locationSearchTarget.contains(event.target)) {
      this.hideLocationResults()
    }
  }

  // ===========================================
  // STEP 4: PHOTO HANDLING
  // ===========================================

  handlePhotoSelect(event) {
    const files = Array.from(event.target.files)
    const maxPhotos = 4

    // Limit to 4 photos
    const newFiles = files.slice(0, maxPhotos - this.photos.length)
    this.photos = [...this.photos, ...newFiles].slice(0, maxPhotos)

    this.renderPhotosPreviews()
    this.uploadTempPhotos(newFiles)
  }

  renderPhotosPreviews() {
    if (this.photos.length === 0) {
      this.photoPreviewsContainerTarget.classList.add('hidden')
      return
    }

    this.photoPreviewsContainerTarget.classList.remove('hidden')
    this.photoCountTarget.textContent = this.photos.length

    this.photoPreviewsTarget.innerHTML = this.photos.map((file, index) => {
      const url = URL.createObjectURL(file)
      return `
        <div class="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
          <img src="${url}" class="w-full h-full object-cover" alt="Photo ${index + 1}">
          <button type="button"
                  data-action="click->onboarding-wizard#removePhoto"
                  data-index="${index}"
                  class="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors">
            <i class="fas fa-times text-xs"></i>
          </button>
          ${index === 0 ? '<span class="absolute bottom-2 left-2 px-2 py-1 bg-primary text-white text-xs rounded">' + (this.localeValue === 'el' ? 'Κύρια' : 'Main') + '</span>' : ''}
        </div>
      `
    }).join('')
  }

  removePhoto(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.photos.splice(index, 1)
    this.photoBlobs.splice(index, 1)
    this.renderPhotosPreviews()
    this.saveDraft()
  }

  skipPhotos() {
    this.photos = []
    this.photoBlobs = []
    this.nextStep()
  }

  uploadTempPhotos(files) {
    const url = this.directUploadUrlValue
    for (const file of files) {
      const upload = new DirectUpload(file, url)
      upload.create((error, blob) => {
        if (error) {
          console.error('Photo upload error:', error)
        } else {
          this.photoBlobs.push(blob.key)
          this.saveDraft()
        }
      })
    }
  }

  // ===========================================
  // STEP 5: PRODUCT PREVIEW
  // ===========================================

  updateProductPreview() {
    // Title
    this.previewTitleTarget.textContent = this.productNameTarget.value || 'Product Name'

    // Price
    const price = parseFloat(this.priceTarget.value) || 0
    this.previewPriceTarget.textContent = `${this.currencySymbolValue}${price.toFixed(2)}`

    // Unit
    const unit = this.unitTarget.value || 'kg'
    this.previewUnitTarget.textContent = `/${this.getUnitLabel(unit)}`

    // Location
    const location = this.locationSearchTarget.value || this.locationCityTarget.value
    this.previewLocationTarget.textContent = location || (this.localeValue === 'el' ? 'Τοποθεσία' : 'Location')

    // Category
    this.previewCategoryTarget.textContent = this.selectedCategoryName || (this.localeValue === 'el' ? 'Κατηγορία' : 'Category')

    // Quantity
    const quantity = parseInt(this.quantityTarget.value) || 0
    const availableText = this.localeValue === 'el' ? 'διαθέσιμα' : 'available'
    this.previewQuantityTarget.textContent = `${quantity} ${availableText}`

    // Image
    if (this.photos.length > 0) {
      const url = URL.createObjectURL(this.photos[0])
      this.previewImageTarget.src = url
      this.previewImageTarget.classList.remove('hidden')
      if (this.hasPreviewImagePlaceholderTarget) {
        this.previewImagePlaceholderTarget.classList.add('hidden')
      }
    } else {
      this.previewImageTarget.classList.add('hidden')
      if (this.hasPreviewImagePlaceholderTarget) {
        this.previewImagePlaceholderTarget.classList.remove('hidden')
      }
    }
  }

  // ===========================================
  // STEP 6: OAUTH & SIGNUP
  // ===========================================

  toggleEmailForm() {
    const isHidden = this.emailFormTarget.classList.contains('hidden')
    this.emailFormTarget.classList.toggle('hidden')
    this.emailToggleIconTarget.classList.toggle('fa-chevron-down', isHidden)
    this.emailToggleIconTarget.classList.toggle('fa-chevron-up', !isHidden)
  }

  async saveBeforeOAuth(event) {
    // Prevent the form from submitting immediately
    event.preventDefault()

    // Save draft to localStorage
    this.saveDraft()

    // Save draft to server session, then submit the form
    await this.saveDraftToSession()

    // Now submit the form (the button is inside a form created by button_to)
    const form = event.target.closest('form')
    if (form) {
      form.submit()
    }
  }

  async saveDraftToSession() {
    const draft = this.buildDraftData()

    try {
      const locale = this.localeValue === 'en' ? '/en' : ''
      await fetch(`${locale}/campaigns/fb_welcome/save_draft`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ draft })
      })
    } catch (error) {
      console.error('Failed to save draft to session:', error)
    }
  }

  toggleLoginMode() {
    this.isLoginMode = !this.isLoginMode
    this.updateEmailFormMode()
  }

  updateEmailFormMode() {
    const createText = this.localeValue === 'el' ? 'Δημιουργία Λογαριασμού & Δημοσίευση' : 'Create Account & Publish'
    const loginText = this.localeValue === 'el' ? 'Σύνδεση & Δημοσίευση' : 'Login & Publish'
    const haveAccountText = this.localeValue === 'el' ? 'Έχεις ήδη λογαριασμό;' : 'Already have an account?'
    const createAccountText = this.localeValue === 'el' ? 'Δεν έχεις λογαριασμό;' : "Don't have an account?"
    const loginLinkText = this.localeValue === 'el' ? 'Σύνδεση' : 'Login'
    const signupLinkText = this.localeValue === 'el' ? 'Εγγραφή' : 'Sign up'

    if (this.hasEmailSubmitButtonTarget) {
      this.emailSubmitButtonTarget.textContent = this.isLoginMode ? loginText : createText
    }
    if (this.hasLoginToggleTarget) {
      this.loginToggleTarget.textContent = this.isLoginMode ? signupLinkText : loginLinkText
      // Update the text before the link
      const parentP = this.loginToggleTarget.parentElement
      if (parentP) {
        parentP.childNodes[0].textContent = this.isLoginMode ? createAccountText + ' ' : haveAccountText + ' '
      }
    }

    // Clear any error messages
    if (this.hasEmailErrorTarget) {
      this.emailErrorTarget.classList.add('hidden')
    }
  }

  async submitEmailSignup() {
    const email = this.emailTarget.value.trim()
    const password = this.passwordTarget.value

    if (!email || !password) {
      this.showEmailError(this.localeValue === 'el' ? 'Συμπληρώστε email και κωδικό' : 'Please fill in email and password')
      return
    }

    // For signup mode, validate password length
    if (!this.isLoginMode && password.length < 8) {
      this.showEmailError(this.localeValue === 'el' ? 'Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες' : 'Password must be at least 8 characters')
      return
    }

    // Save draft first
    await this.saveDraftToSession()

    // Determine which endpoint to use
    const endpoint = this.isLoginMode ? 'email_login' : 'email_signup'

    try {
      const locale = this.localeValue === 'en' ? '/en' : ''
      const response = await fetch(`${locale}/campaigns/fb_welcome/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ email, password })
      })

      const data = await response.json()

      if (data.success) {
        // Shared attribution props (locale/region/user_role/campaign_source)
        // set on <body> by plausible_default_props — campaign_source carries
        // any landing-page src so both trackers attribute consistently.
        const props = JSON.parse(document.body.dataset.plausibleProps || '{}')
        // FB Pixel — standard event, fires only on signup (not login).
        // Inline so it can't be broken by a future JSON→Turbo-Stream
        // refactor. try/catch means a broken Meta SDK can never strand
        // the user mid-onboarding: the redirect always proceeds. `source`
        // lets Meta segment by campaign (unknown custom keys are ignored).
        try {
          if (!this.isLoginMode && typeof fbq !== 'undefined') {
            fbq('track', 'CompleteRegistration', { source: props.campaign_source })
          }
        } catch (e) { /* swallow — tracking never blocks the redirect */ }
        // Plausible — defer the redirect until the beacon fires (or
        // 500ms timeout). Without the callback the immediate
        // window.location.href = ... kills the outgoing beacon queue
        // before Plausible receives the event, which is why the
        // Signed Up goal showed 0 conversions on 2026-07-14 despite
        // 4 real signups in the DB. 500ms setTimeout is a safety net
        // so a stalled Plausible SDK can never strand the user
        // mid-onboarding — redirect always fires within half a second.
        // `redirected` flag prevents double navigation if callback +
        // timeout both fire. Skips entirely when isLoginMode (returning
        // user login, not a fresh registration) — matches the fbq gate.
        let redirected = false
        const doRedirect = () => {
          if (redirected) return
          redirected = true
          window.location.href = data.redirect_url
        }
        try {
          if (!this.isLoginMode && typeof plausible !== 'undefined') {
            plausible('CompleteRegistration', { props, callback: doRedirect })
            setTimeout(doRedirect, 500)
          } else {
            doRedirect()
          }
        } catch (e) { doRedirect() }
      } else if (data.error_code === 'email_in_use' && !this.isLoginMode) {
        // Existing account — auto-switch to login mode AND auto-retry
        // with the password the user already typed. From the user's
        // perspective they typed a password intending to use their
        // account; whether the backend routes it through /email_signup
        // or /email_login is an implementation detail they don't care
        // about. First iteration of this fix asked the user to re-enter
        // the password after the mode switch — real user testing showed
        // they consistently had to submit twice with the same correct
        // password to actually log in. See docs/bugs.md for the report.
        //
        // Recursive call chain is safe: once isLoginMode=true, the next
        // call hits /email_login which returns wrong_password or
        // user_not_found (never email_in_use), so this branch cannot
        // re-enter itself.
        //
        // Correct password → user logs in and product publishes. Wrong
        // password → the else branch below shows the login error and the
        // user can retry with the right one.
        this.isLoginMode = true
        this.updateEmailFormMode()
        return this.submitEmailSignup()
      } else {
        this.showEmailError(data.error || (this.localeValue === 'el' ? 'Κάτι πήγε στραβά' : 'Something went wrong'))
      }
    } catch (error) {
      console.error('Auth error:', error)
      this.showEmailError(this.localeValue === 'el' ? 'Κάτι πήγε στραβά. Παρακαλώ δοκιμάστε ξανά.' : 'Something went wrong. Please try again.')
    }
  }

  showEmailError(message) {
    if (this.hasEmailErrorTarget) {
      this.emailErrorTarget.textContent = message
      this.emailErrorTarget.classList.remove('hidden')
    } else {
      alert(message)
    }
  }

  // ===========================================
  // DRAFT PERSISTENCE (localStorage)
  // ===========================================

  buildDraftData() {
    return {
      current_step: this.currentStep,
      started_at: this.draft?.started_at || new Date().toISOString(),
      last_updated_at: new Date().toISOString(),
      product_name: this.productNameTarget.value,
      category_id: this.categoryIdTarget.value,
      category_name: this.selectedCategoryName,
      price: this.priceTarget.value,
      unit: this.unitTarget.value,
      quantity: this.quantityTarget.value,
      location_description: this.locationSearchTarget.value,
      location_city: this.locationCityTarget.value,
      location_region: this.locationRegionTarget.value,
      location_city_en: this.locationCityEnTarget.value,
      location_city_el: this.locationCityElTarget.value,
      location_region_en: this.locationRegionEnTarget.value,
      location_region_el: this.locationRegionElTarget.value,
      latitude: this.latitudeTarget.value,
      longitude: this.longitudeTarget.value,
      pickup_details: this.pickupDetailsTarget.value,
      temp_image_keys: this.photoBlobs,
      photos_skipped: this.photos.length === 0 && this.currentStep > 4
    }
  }

  saveDraft() {
    const draft = this.buildDraftData()
    localStorage.setItem('trofika_onboarding_draft', JSON.stringify(draft))
    this.draft = draft
  }

  loadDraft() {
    const stored = localStorage.getItem('trofika_onboarding_draft')
    return stored ? JSON.parse(stored) : null
  }

  restoreDraft() {
    if (!this.draft) return

    // Restore step FIRST so UI can be updated correctly
    if (this.draft.current_step) {
      this.currentStep = this.draft.current_step
    }

    // Restore form values (wrapped in try/catch to prevent partial failures)
    try {
      if (this.draft.product_name) this.productNameTarget.value = this.draft.product_name
      if (this.draft.category_id) {
        this.categoryIdTarget.value = this.draft.category_id
        this.selectedCategoryName = this.draft.category_name

        // Highlight selected category
        const categoryButton = this.categoryGridTarget.querySelector(`[data-category-id="${this.draft.category_id}"]`)
        if (categoryButton) {
          categoryButton.classList.remove('border-gray-200')
          categoryButton.classList.add('border-primary', 'bg-primary')
          categoryButton.querySelector('i').classList.remove('text-primary')
          categoryButton.querySelector('i').classList.add('text-white', 'opacity-80')
          categoryButton.querySelector('div').classList.remove('text-text-primary')
          categoryButton.querySelector('div').classList.add('text-white', 'opacity-80')
        }
      }

      if (this.draft.price) this.priceTarget.value = this.draft.price
      if (this.draft.unit) {
        this.unitTarget.value = this.draft.unit
        // Highlight selected unit
        const unitButton = this.unitGridTarget.querySelector(`[data-unit="${this.draft.unit}"]`)
        if (unitButton) {
          this.unitGridTarget.querySelectorAll('.unit-pill').forEach(pill => {
            pill.classList.remove('border-primary', 'bg-primary', 'text-white')
            pill.classList.add('border-gray-200', 'text-text-primary')
          })
          unitButton.classList.remove('border-gray-200', 'text-text-primary')
          unitButton.classList.add('border-primary', 'bg-primary', 'text-white')
        }
      }
      if (this.draft.quantity) this.quantityTarget.value = this.draft.quantity

      if (this.draft.location_description) {
        this.locationSearchTarget.value = this.draft.location_description
        this.selectedLocationTextTarget.textContent = this.draft.location_description
        this.selectedLocationTarget.classList.remove('hidden')
      }
      if (this.draft.latitude) this.latitudeTarget.value = this.draft.latitude
      if (this.draft.longitude) this.longitudeTarget.value = this.draft.longitude
      if (this.draft.location_city) this.locationCityTarget.value = this.draft.location_city
      if (this.draft.location_region) this.locationRegionTarget.value = this.draft.location_region
      if (this.draft.location_city_en) this.locationCityEnTarget.value = this.draft.location_city_en
      if (this.draft.location_city_el) this.locationCityElTarget.value = this.draft.location_city_el
      if (this.draft.location_region_en) this.locationRegionEnTarget.value = this.draft.location_region_en
      if (this.draft.location_region_el) this.locationRegionElTarget.value = this.draft.location_region_el
      if (this.draft.pickup_details) this.pickupDetailsTarget.value = this.draft.pickup_details

      if (this.draft.temp_image_keys) this.photoBlobs = this.draft.temp_image_keys

      // Update preview
      this.updatePreview()
    } catch (error) {
      console.error('Error restoring draft form values:', error)
    }
  }

  clearDraft() {
    localStorage.removeItem('trofika_onboarding_draft')
    this.draft = null
  }
}
